<template>
    <Select
        ref="selectComponent"
        v-model="model"
        :items="federations"
        item-key="id"
        :placeholder="translate('federation_select.select_federation')"
        :search-placeholder="translate('federation_select.search_federation')"
        :no-results-text="translate('federation_select.no_federations_found')"
        :dropdown-mode="dropdownMode"
        :searchable="true"
        :aria-label="translate('federation_select.select_federation')"
        :has-error="true"
        :allow-cancel-choice="allowCancelChoice"
    >
        <template v-slot:cancel-choice>
            <span class="icon">
                <i class="fas fa-list"></i>
            </span>
            <span>{{ translate("common.show_all") }}</span>
        </template>

        <template v-slot:selected-item="{ selectedItem }">
            <div class="federation-item" v-if="selectedItem">
                <div v-if="selectedItem.logoUrl" class="federation-logo">
                    <img :src="selectedItem.logoUrl" alt="Federation logo" />
                </div>
                <div v-else class="federation-logo federation-logo-placeholder">
                    <i class="fas fa-building"></i>
                </div>
                <span class="federation-name federation-name-limited-width is-text-overflow-only-desktop">
                    {{ selectedItem.federationName }}
                </span>
            </div>
        </template>

        <template v-slot:item="{ item }">
            <div class="federation-item">
                <div v-if="item.logoUrl" class="federation-logo">
                    <img :src="item.logoUrl" alt="Federation logo" />
                </div>
                <div v-else class="federation-logo federation-logo-placeholder">
                    <i class="fas fa-building"></i>
                </div>
                <span class="federation-name">{{ item.federationName }}</span>
            </div>
        </template>

        <template v-slot:no-results>
            <div class="has-text-centered">
                <span class="icon">
                    <i class="fas fa-exclamation-triangle"></i>
                </span>
                <span>{{ translate("federation_select.no_federations_found") }}</span>
            </div>
            <div class="has-text-centered mt-2">
                <a @click="onNewFederationClick">
                    <span class="icon">
                        <i class="fas fa-plus-circle"></i>
                    </span>
                    <span>{{ translate("federation.click_here_to_create") }}</span>
                </a>
            </div>
        </template>
    </Select>
</template>

<script setup lang="ts">
    import { computed, watch } from "vue";
    import { translate } from "../../Common/translations";
    import Select from "../../Common/Select.vue";
    import { ref, onMounted } from "vue";
    import { handleError } from "../../Common/errorHandler";
    import localStorage from "../../Common/store";
    import { ComponentExposed } from "vue-component-type-helpers";
    import { useFederationStore } from "../Listing/federationStore";

    const model = defineModel<string | null>();

    const props = defineProps<{
        dropdownMode?: "absolute" | "container";
        rememberSelectionKey?: string;
    }>();

    const federationStore = useFederationStore();

    const selectComponent = ref<ComponentExposed<typeof Select> | null>(null);
    const federations = computed(() => federationStore.userFederations);
    const isLoading = ref(true);
    const allowCancelChoice = ref(true);

    const emit = defineEmits(["newFederationClick"]);

    function onNewFederationClick(e) {
        e.preventDefault();
        emit("newFederationClick");
        selectComponent.value?.close();
    }

    onMounted(() => {
        const selectedFederationId = props.rememberSelectionKey ? localStorage.getString(props.rememberSelectionKey) : null;

        return federationStore
            .fetchFederationsForUser()
            .then(() => {
                allowCancelChoice.value = federations.value.length > 0;

                // Priority 1: Auto-preselect single unapproved federation (new organizer)
                const singleUnapproved = federationStore.singleUnapprovedFederation;
                if (singleUnapproved) {
                    model.value = singleUnapproved.id;
                    if (props.rememberSelectionKey) {
                        localStorage.setString(props.rememberSelectionKey, singleUnapproved.id);
                    }
                    return;
                }

                // Priority 2: Restore from localStorage
                if (selectedFederationId && props.rememberSelectionKey) {
                    const exists = federations.value.find(fed => fed.id === selectedFederationId);
                    if (exists) {
                        model.value = selectedFederationId;
                    }
                }
            })
            .catch(handleError)
            .finally(() => {
                isLoading.value = false;
            });
    });

    watch(
        () => model.value,
        newVal => {
            if (!props.rememberSelectionKey) {
                return;
            }
            localStorage.setString(props.rememberSelectionKey, newVal ?? undefined);
        },
    );
</script>

<style scoped></style>
